/**
 * WhatsNew service
 * Service for fetching what's new data from the API
 */
import type {
  WhatsNewRequest,
  WhatsNewResponse,
  WhatsNewErrorResponse,
  WhatsNewUpdatePayload,
  WhatsNew,
  ReachSeries,
  WhatsNewReadingSummary,
  WhatsNewViewersResponse,
} from '../Types/WhatsNew.types';
import type { ICreateWhatsNewParams } from './WhatsNew.types.service';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const WHATS_NEW_ENDPOINT = '/v1/whats-new';

/**
 * Get what's new items with filters and pagination
 */
export const getWhatsNew = async (params: WhatsNewRequest = {}): Promise<WhatsNewResponse> => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();

    if (params.platformVersionId) queryParams.append('platformVersionId', params.platformVersionId);
    if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.useCaseSlug) queryParams.append('useCaseSlug', params.useCaseSlug);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to fetch what's new items",
        statusCode: response.status,
      }));

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to view what's new items");
      }

      throw new Error(errorData.message || "Failed to fetch what's new items");
    }

    const data: WhatsNewResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Get a single what's new item by ID
 */
export const getWhatsNewById = async (id: string): Promise<WhatsNew> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to fetch what's new item",
        statusCode: response.status,
      }));

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to view what's new items");
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to fetch what's new item");
    }

    const data: WhatsNew = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Update a what's new item (full update support)
 * Returns the updated WhatsNew object from the server
 */
export const updateWhatsNew = async (
  id: string,
  payload: WhatsNewUpdatePayload,
): Promise<WhatsNew> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to update what's new item",
        statusCode: response.status,
      }));

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to update what's new items");
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to update what's new item");
    }

    const data: WhatsNew = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while updating what's new item");
  }
};

/**
 * Create a new what's new item
 * Returns the created WhatsNew object
 */
export const createWhatsNew = async (payload: ICreateWhatsNewParams): Promise<WhatsNew> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to create what's new item",
        statusCode: response.status,
      }));

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to create what's new items");
      }

      throw new Error(errorData.message || "Failed to create what's new item");
    }

    const data: WhatsNew = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while creating what's new item");
  }
};

/**
 * Toggle active status of a what's new item
 * Returns the updated WhatsNew object
 */
export const toggleWhatsNewActive = (id: string, isActive: boolean): Promise<WhatsNew> => {
  return updateWhatsNew(id, { isActive });
};

/**
 * Delete a what's new item by ID
 */
export const deleteWhatsNew = async (id: string): Promise<void> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to delete what's new item",
        statusCode: response.status,
      }));

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to delete what's new items");
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to delete what's new item");
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while deleting what's new item");
  }
};

/**
 * Submit a what's new item for approval
 * @param id The what's new item ID
 * @param publishedAt Optional scheduled publish date
 */
export const submitWhatsNewForApproval = async (
  id: string,
  publishedAt?: string,
): Promise<{ message: string; whatsNew: WhatsNew }> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}/submit`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(publishedAt ? { publishedAt } : {}),
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to submit what's new for approval",
        statusCode: response.status,
      }));

      // Handle 400 Bad Request
      if (response.status === 400) {
        throw new Error(errorData.message || 'Invalid status for submission');
      }

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to submit what's new items");
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to submit what's new for approval");
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while submitting what's new for approval");
  }
};

/**
 * Approve a what's new item
 * @param id The what's new item ID
 */
export const approveWhatsNew = async (
  id: string,
): Promise<{ message: string; whatsNew: WhatsNew }> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}/approve`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to approve what's new",
        statusCode: response.status,
      }));

      // Handle 400 Bad Request
      if (response.status === 400) {
        throw new Error(errorData.message || 'Invalid status for approval');
      }

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error(
          errorData.message ||
            "You do not have permission to approve what's new items or cannot approve your own submission",
        );
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to approve what's new");
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while approving what's new");
  }
};

/**
 * Reject a what's new item
 * @param id The what's new item ID
 * @param rejectionReason Reason for rejection (required, max 500 chars)
 */
export const rejectWhatsNew = async (
  id: string,
  rejectionReason: string,
): Promise<{ message: string; whatsNew: WhatsNew }> => {
  try {
    const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}/reject`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rejectionReason }),
    });

    if (!response.ok) {
      const errorData: WhatsNewErrorResponse = await response.json().catch(() => ({
        message: "Failed to reject what's new",
        statusCode: response.status,
      }));

      // Handle 400 Bad Request
      if (response.status === 400) {
        throw new Error(errorData.message || 'Invalid status for rejection');
      }

      // Handle 403 Forbidden
      if (response.status === 403) {
        throw new Error("You do not have permission to reject what's new items");
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error("What's new item not found");
      }

      throw new Error(errorData.message || "Failed to reject what's new");
    }

    const data = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while rejecting what's new");
  }
};

/**
 * Daily distinct readers over a window, with the preceding window for comparison.
 *
 * @param params - `from`/`to` are inclusive ISO dates; both default to the last 30 days.
 *   Omit `tenantSlug` to aggregate every tenant.
 */
/**
 * Reading time across every announcement, in one request.
 *
 * For the overview screen. Asking each announcement's own `/stats` would be one request per card.
 */
export const getWhatsNewReadingSummary = async (): Promise<WhatsNewReadingSummary> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/stats/summary`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (!response.ok) {
    throw new Error('Não foi possível carregar o tempo de leitura');
  }

  return (await response.json()) as WhatsNewReadingSummary;
};

export const getWhatsNewReachSeries = async (
  params: { from?: string; to?: string; tenantSlug?: string } = {},
): Promise<ReachSeries> => {
  const queryParams = new URLSearchParams();
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);

  const queryString = queryParams.toString();
  const url = `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/reach-series${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch reach series');
  }

  return (await response.json()) as ReachSeries;
};

/** Who opened an announcement, most recent first. */
export const getWhatsNewViewers = async (
  id: string,
  params: { page?: number; limit?: number } = {},
): Promise<WhatsNewViewersResponse> => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', String(params.page));
  if (params.limit) query.append('limit', String(params.limit));

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}/viewers${suffix}`,
  );

  if (!response.ok) {
    throw new Error('Não foi possível carregar quem viu este anúncio.');
  }

  return (await response.json()) as WhatsNewViewersResponse;
};

/**
 * Forget that one person saw an announcement, so it is shown to them again.
 *
 * Behind `whats_new:reset_viewer` on the BFF, which only super admins hold — it reaches another
 * person's data rather than the announcement's.
 */
export const resetWhatsNewViewer = async (
  id: string,
  platformUserId: number,
): Promise<{ message: string }> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${WHATS_NEW_ENDPOINT}/${id}/viewers/${platformUserId}`,
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const error: { message?: string } = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Não foi possível reexibir o anúncio para essa pessoa.');
  }

  return (await response.json()) as { message: string };
};
